import Drawer from "components/drawerSlide/drawer";
import FormLayout from "components/form/FormLayout";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "components/icon/Icon";
import { Select } from "components/select/index";
import { convertHoles } from "helpers/Helpers";
import { faMinus, faPlus, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment, { MomentInput } from "moment";
import Checkbox from "components/form/checkbox/Checkbox";
import { IDivision, IFacility } from "redux/reducers/models/facility";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { IBookingCategory } from "redux/reducers/models/teetime";
import ReactDOM from "react-dom";
import { useSearch } from "hooks/useSearch/useSearch";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import "../TeeSheetTemplate.scss";
import BookingCategoryTagsSelect from "./BookingCategoryTagsSelect";

export interface ITeeTimeSettingsState {
    turn_division_id: string;
    pre_paid_required: boolean;
    credit_card_required: boolean;
    web_booking_enabled: boolean;
    bookings_prevent_guests: boolean;
    blocked_type: string;
    cart_rule: string;
    league_id: string;
    tournament_id: string;
    divisions_available: number[];
    booking_category_ids: number[];
}

interface IProps {
    open: boolean;
    facility: IFacility;
    bookingCategories: Array<IBookingCategory>;
    divisions: Array<IDivision>;
    courseId: number;
    onOk: (teeTimeSettings: ITeeTimeSettingsState & {tournament?: ITournament; league?: ILeague}) => void;
    onClose: () => void;
    subtitle?: string;
}

export default function EditTeeTimeDrawer(props: IProps) {
    const { open, facility, courseId, onOk, onClose, subtitle, bookingCategories, divisions } = props;
    const { Option } = Select;
    const leaguesHook = useSearch<ILeague>({
        fetchAll: () => GetLeague(null, false).then(res => res.data),
        searchFn: search =>
          GetLeague({ search }, false).then(res => res.data),
      });
      
      const tournamentsHook = useSearch<ITournament>({
        fetchAll: () => GetTournament(null, false).then(res => res.data),
        searchFn: search =>
          GetTournament({ search }, false).then(res => res.data),
      });

      const leagues = leaguesHook.displayItems;
      const tournaments = tournamentsHook.displayItems;


    const [state, setState] = useState<ITeeTimeSettingsState>({
        turn_division_id: "",
        pre_paid_required: null,
        credit_card_required: null,
        web_booking_enabled: null,
        blocked_type: "",
        cart_rule: "",
        league_id: "",
        tournament_id: "",
        divisions_available: [],
        booking_category_ids: [],
        bookings_prevent_guests: null,

    });
    const [displayState, setDisplayState] = useState({
        displayEvents: true,
        displayRestrictions: true,
        displayPaymentRequirements: true,
    });
    const [eventState, setEventState] = useState<{selectedTournament: ITournament; selectedLeague: ILeague;}>({
        selectedLeague: null,
        selectedTournament: null,
    });
    const {t} = useTranslation();

    const divisionOptions = [
        { label: `${convertHoles(9, facility?.courses?.find(course => course?.id === courseId))} ${t("Holes")}`, value: 1 },
        { label: `${convertHoles(18, facility?.courses?.find(course => course?.id === courseId))} ${t("Holes")}`, value: 2 },
      ];

      const blockedTypeOptions = [
        // { label: "", value: "" },
        { label: t("Open"), value: "open" },
        { label: t("Blocked"), value: "blocked" },
        { label: t("Turn"), value: "crossover" },
      ];
    
      const cartRuleOptions = [
        // { label: "", value: "" },
        { label: t("None"), value: "none" },
        { label: t("Optional"), value: "optional" },
        { label: t("Required"), value: "required" },
      ];



    function handleCheckboxChange(event: any) {
        const { id, checked, value } = event.target;
        if (!checked) {
            setState(prevState => ({ ...prevState, [id]: null }));
        } else {
            setState(prevState => ({ ...prevState, [id]: value === "true" ? checked : !checked }));
        }
      }

    function handleDivisionsAvailableChange(event: React.ChangeEvent<HTMLInputElement>) {
        const value = parseInt(String(event?.target?.value));
        if (isNaN(value)) {
          return;
        }
    
        if (state?.divisions_available.find(divisionsAvailable => divisionsAvailable === value) === undefined) {
            setState(prevState => ({
            ...prevState,
            divisions_available: [...prevState.divisions_available, value].sort((a, b) => a - b),
          }));
        } else {
          const updatedDivisionsAvailable = state?.divisions_available.filter(
            divisionsAvailable => divisionsAvailable !== value,
          );
          setState(prevState => ({
            ...prevState,
            divisions_available: updatedDivisionsAvailable.sort((a, b) => a - b),
          }));
        }
      }

      function removeSelectedTournament() {
        ReactDOM.unstable_batchedUpdates(() => {
            setEventState(prev => ({...prev, selectedTournament: null}));
            setState(prevState => ({ ...prevState, tournament_id: "" }));
        });
      }

      function removeSelectedLeague() {
        ReactDOM.unstable_batchedUpdates(() => {
            setEventState(prev => ({...prev, selectedLeague: null}));
            setState(prevState => ({ ...prevState, league_id: "" }));
        });
      }

      function handleTournamentChange(id: string, tournament: ITournament) {
        setState(prevState => ({ ...prevState, [id]: tournament.id }));
        setEventState(prevState => ({ ...prevState, selectedTournament: tournament }));
      }
    
      function handleLeagueChange(id: string, league: ILeague) {
        setState(prevState => ({ ...prevState, [id]: league.id }));
        setEventState(prevState => ({ ...prevState, selectedLeague: league }));
      }

      function clearDrawer() {
        ReactDOM.unstable_batchedUpdates(() => {
          setState(prev => ({
            ...prev,
            turn_division_id: "",
            pre_paid_required: null,
            credit_card_required: null,
            web_booking_enabled: null,
            blocked_type: "",
            cart_rule: "",
            league_id: "",
            tournament_id: "",
            divisions_available: [],
            booking_category_ids: [],
            bookings_prevent_guests: null,
          }));
          setEventState(prevState => ({
            ...prevState,
            selectedTournament: null,
            selectedLeague: null,
          }));
        });
      }

      function handleClose() {
        void clearDrawer();
        void onClose();
      }

      function handleOk() {
        void clearDrawer();
        void onOk({...state, league: eventState?.selectedLeague, tournament: eventState?.selectedTournament});
      }


    return (
        <Drawer
        open={open}
        title={t("Apply settings")}
        subtitle={subtitle ?? undefined}
        onClose={handleClose}
        onOk={() => handleOk()}
        okText={t("Apply")}
        backDropClose={true}
        hideBackdrop
        showCancel={true}
        cancelText="Cancel"
        cancelButtonType="text"
        secondaryActions={[{label: t("Clear all"), action: () => clearDrawer()}]}
      >
        <Drawer.Section bordered>
        <h1>{t("Tee sheet setup")}</h1>
        <br />
        <FormLayout>
            <FormLayout.Group>
            <div className="text-base font-medium text-black block">
            <label className="ui-teesheet-template-label">{t("Block type")}</label>
            <div className="flex items-center gap-2">
            {blockedTypeOptions.map((type, index: number) => {
                return (
                <Checkbox
                    key={index}
                    id="blocked_type"
                    size="medium"
                    value="false"
                    checked={state.blocked_type !== null && state?.blocked_type === type?.value}
                    onChange={() => {
                        let typeToUpdate = state?.blocked_type;
                        if (state?.blocked_type === type?.value) {
                            typeToUpdate = null;
                        } else {
                            typeToUpdate = type?.value;
                        }
                        setState(prev => ({...prev, blocked_type: typeToUpdate}));
                    }}
                    label={type?.label}
                    selectionOutline
                    circle
                />
                );
             })}
             </div>
             </div>
            </FormLayout.Group>
            <FormLayout.Group>
            <div className="text-base font-medium text-black block">
            <label className="ui-teesheet-template-label">{t("Cart rule")}</label>
            <div className="flex items-center gap-2">
            {cartRuleOptions?.map((type, index: number) => {
                return (
                <Checkbox
                    key={index}
                    id="cart_rule"
                    size="medium"
                    value="false"
                    checked={state.cart_rule !== null && state?.cart_rule === type?.value}
                    onChange={() => {
                        let typeToUpdate = state?.cart_rule;
                        if (state?.cart_rule === type?.value) {
                            typeToUpdate = null;
                        } else {
                            typeToUpdate = type?.value;
                        }
                        setState(prev => ({...prev, cart_rule: typeToUpdate}));
                    }}
                    label={type?.label}
                    selectionOutline
                    circle
                />
                );
             })}
             </div>
             </div>
            </FormLayout.Group>
            <FormLayout.Group>
              <BookingCategoryTagsSelect
                bookingCategories={bookingCategories}
                selectedIds={state.booking_category_ids}
                onChange={booking_category_ids => setState(prev => ({ ...prev, booking_category_ids }))}
              />
            </FormLayout.Group>
            <FormLayout.Group>
            <div>
                <label className="ui-teesheet-template-label">{t('Holes available')}</label>
                <div className="flex items-center gap-2">
                {divisionOptions.map((divisionOption, index: number) => {
                    return (
                    <Checkbox
                        key={index}
                        size="medium"
                        onChange={handleDivisionsAvailableChange}
                        value={divisionOption.value}
                        checked={
                        state.divisions_available.find(
                            divisionsAvailable => divisionsAvailable === divisionOption.value,
                        ) !== undefined
                        }
                        label={divisionOption.label}
                        selectionOutline
                    />
                    );
                })}
                </div>
                </div>
            </FormLayout.Group>
            <FormLayout.Group>
            <div className="text-base font-medium text-black block">
                <label className="ui-teesheet-template-label">{"Web booking enabled"}</label>
                <div className="flex items-center gap-2">
                <Checkbox
                    id="web_booking_enabled"
                    size="medium"
                    value="true"
                    checked={state.web_booking_enabled !== null && state.web_booking_enabled}
                    onChange={handleCheckboxChange}
                    label={t("Yes")}
                    selectionOutline
                />
                <Checkbox
                    id="web_booking_enabled"
                    size="medium"
                    value="false"
                    checked={state.web_booking_enabled !== null && !state.web_booking_enabled}
                    onChange={handleCheckboxChange}
                    label={t("No")}
                    selectionOutline
                />
                </div>
            </div>
            </FormLayout.Group>
        </FormLayout>
        </Drawer.Section>
        <Drawer.Section bordered>
            <div className="flex item-center justify-between gap-2">
                <h1>{t("Events")}</h1>
                <FontAwesomeIcon icon={displayState?.displayEvents ? faMinus : faPlus} className="text-bold cursor-pointer" onClick={() => setDisplayState(prev => ({...prev, displayEvents: !prev?.displayEvents}))}/>
            </div>
            <br />
            {displayState?.displayEvents ? (
                    <FormLayout>
                    <FormLayout.Group>
                        {eventState.selectedTournament ? (
                    <div>
                      <div>
                        <p className="event-label">{t('Tournaments')}</p>
                      </div>

                      <div className="selected-container ">
                        <div className="event-name">
                          <div>{eventState?.selectedTournament?.name}</div>

                          <div>
                            <button className="" onClick={removeSelectedTournament}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Select
                        showSearch
                        label={t("Tournaments")}
                        onSearch={(value: string) => tournamentsHook.setSearchValue(value)}
                        onChange={(_, tournament: ITournament) =>
                          handleTournamentChange("tournament_id", tournament)
                        }
                        placeholder={t("Search tournaments...")}
                        allowClear
                        searchValue={tournamentsHook.searchValue}
                        showDropDownOnFocus={true}
                        searching={tournamentsHook.loading}
                      >
                        <Option
                          value={"none"}
                          extraValues={{
                            name: t("None"),
                            id: "none",
                          }}
                        >
                          <div className="">{t("None")}</div>
                        </Option>

                        {tournaments?.filter(tournament => !moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: any, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date as MomentInput).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}

                        <Option disabled value={-2} key={-2} name={""}>
                          <div>
                            <hr style={{ margin: "0px" }} />
                          </div>
                        </Option>

                        {tournaments?.filter(tournament => moment(tournament.date).isBefore(moment(), "day"))
                          .map((tournament: any, index: number) => {
                            return (
                              <Option key={index} value={tournament.id} extraValues={tournament}>
                                <div className="flex items-center">
                                  <span>{tournament.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(tournament.date as MomentInput).format("LL")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}
                      </Select>
                    </div>
                  )}
                        </FormLayout.Group>
                        <FormLayout.Group>
                        {eventState?.selectedLeague ? (
                    <div>
                      <div>
                        <p className="event-label">{t('Leagues')}</p>
                      </div>

                      <div className="selected-container ">
                        <div className="event-name">
                          <div>{eventState?.selectedLeague?.name}</div>

                          <div>
                            <button className="" onClick={removeSelectedLeague}>
                              <Icon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Select
                        showSearch
                        label={t("Leagues")}
                        onSearch={(value: string) => leaguesHook.setSearchValue(value)}
                        onChange={(_, league: ILeague) => handleLeagueChange("league_id", league)}
                        placeholder={t("Search leagues...")}
                        allowClear
                        searchValue={leaguesHook.searchValue}
                        showDropDownOnFocus={true}
                        searching={leaguesHook.loading}
                      >
                        <Option
                          value={"none"}
                          extraValues={{
                            name: t("None"),
                            id: "none",
                          }}
                        >
                          <div className="">{t("None")}</div>
                        </Option>
                        {leagues?.filter(league => league.start_date && !moment(league.start_date).isBefore(moment(), "day"))
                          ?.map((league: any, index: number) => {
                            return (
                              <Option value={league?.id} key={index} extraValues={league}>
                                <div className="">
                                  <span className="">{league.name}</span>
                                  <span className="shotgun-tournament-date">
                                    {moment(league.start_date as MomentInput).format("YYYY")}
                                  </span>
                                </div>
                              </Option>
                            );
                          })}

                        <Option disabled value={-2} key={-2} name={""}>
                          <div>
                            <hr style={{ margin: "0px" }} />
                          </div>
                        </Option>

                        {leagues?.filter(league => !league.start_date || moment(league.start_date).isBefore(moment(), "day"))
                          ?.map((league: any, index: number) => {
                            return (
                              <Option value={league?.id} key={index} extraValues={league}>
                                <div className="">
                                  <span className="">{league.name}</span>
                                  {league?.start_date && (
                                    <span className="shotgun-tournament-date">
                                      {moment(league.start_date as MomentInput).format("YYYY")}
                                    </span>
                                  )}
                                </div>
                              </Option>
                            );
                          })}
                      </Select>
                    </div>
                  )}
                        </FormLayout.Group>
                    </FormLayout>
                    ) : null}
        </Drawer.Section>
        <Drawer.Section bordered>
          <div className="flex item-center justify-between gap-2">
                  <h1>{t("Restrictions")}</h1>
                  <FontAwesomeIcon icon={displayState?.displayRestrictions ? faMinus : faPlus} className="text-bold cursor-pointer" onClick={() => setDisplayState(prev => ({...prev, displayRestrictions: !prev?.displayRestrictions}))}/>
            </div>
            <br />  
            {displayState?.displayRestrictions ? (
              <FormLayout>
                <FormLayout.Group>
                <div className="text-base font-medium text-black block">
                <label className="ui-teesheet-template-label">{t("Turn Division")}</label>
                <div className="flex items-center gap-2">
                {divisions?.map((division, index: number) => {
                return (
                <Checkbox
                    key={index}
                    id="turn_division_id"
                    size="medium"
                    value="false"
                    checked={state.turn_division_id !== null && Number(state?.turn_division_id) === division?.id}
                    onChange={() => {
                        let divisionToUpdate = Number(state?.turn_division_id);
                        if (divisionToUpdate === division?.id) {
                            divisionToUpdate = null;
                        } else {
                            divisionToUpdate = division?.id;
                        }
                        setState(prev => ({...prev, turn_division_id: String(divisionToUpdate)}));
                    }}
                    label={division?.title}
                    selectionOutline
                />
                );
             })}
             </div>
             </div>
                </FormLayout.Group>
                <FormLayout.Group>
            <div className="text-base font-medium text-black block">
                <label className="ui-teesheet-template-label">{t("Allow guests")}</label>
                <div className="flex items-center gap-2">
                <Checkbox
                      id="bookings_prevent_guests"
                      size="small"
                      value="false"
                      checked={state.bookings_prevent_guests !== null && !state.bookings_prevent_guests}
                      onChange={handleCheckboxChange}
                      label={t("Yes")}
                      selectionOutline
                    />
                    <Checkbox
                      id="bookings_prevent_guests"
                      size="small"
                      value="true"
                      checked={state.bookings_prevent_guests !== null && state.bookings_prevent_guests}
                      onChange={handleCheckboxChange}
                      label={t("No")}
                      selectionOutline
                    />
                </div>
            </div>
            </FormLayout.Group>
              </FormLayout>
            ) : null}
        </Drawer.Section>
        <Drawer.Section bordered>
          <div className="flex item-center justify-between gap-2">
                  <h1>{t("Payment requirements")}</h1>
                  <FontAwesomeIcon icon={displayState?.displayPaymentRequirements ? faMinus : faPlus} className="text-bold cursor-pointer" onClick={() => setDisplayState(prev => ({...prev, displayPaymentRequirements: !prev?.displayPaymentRequirements}))}/>
            </div>
            <br />  
            {displayState?.displayPaymentRequirements ? (
              <FormLayout>
                <FormLayout.Group>
                  <div className="text-base font-medium text-black block">
                      <label className="ui-teesheet-template-label">{"Prepaid required"}</label>
                      <div className="flex items-center gap-2">
                      <Checkbox
                      id="pre_paid_required"
                      size="small"
                      value="true"
                      checked={state.pre_paid_required !== null && state.pre_paid_required}
                      onChange={handleCheckboxChange}
                      label={t("Yes")}
                      selectionOutline
                    />
                    <Checkbox
                      id="pre_paid_required"
                      size="small"
                      value="false"
                      checked={state.pre_paid_required !== null && !state.pre_paid_required}
                      onChange={handleCheckboxChange}
                      label={t("No")}
                      selectionOutline
                    />
                      </div>
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <div className="text-base font-medium text-black block">
                      <label className="ui-teesheet-template-label">{"Credit card required"}</label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                        id="credit_card_required"
                        size="small"
                        value="true"
                        checked={state.credit_card_required !== null && state.credit_card_required}
                        onChange={handleCheckboxChange}
                        label={t("Yes")}
                        selectionOutline
                      />
                      <Checkbox
                        id="credit_card_required"
                        size="small"
                        value="false"
                        checked={state.credit_card_required !== null && !state.credit_card_required}
                        onChange={handleCheckboxChange}
                        label={t("No")}
                        selectionOutline
                      />
                      </div>
                  </div>
                </FormLayout.Group>
              </FormLayout>
            ) : null}
        </Drawer.Section>
        </Drawer>
    );
}